import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn(
    'Missing REACT_APP_SUPABASE_URL or REACT_APP_SUPABASE_ANON_KEY. ' +
    'Database features will be disabled. Copy .env.example to .env.local and fill in your keys.'
  );
}

export const supabase = supabaseUrl && supabaseAnonKey
  ? createClient(supabaseUrl, supabaseAnonKey)
  : null;

/**
 * Submit a completed quote to the database and trigger notifications.
 */
/**
 * Insert row, auto-dropping any columns not present in the live schema.
 * Handles schema drift gracefully — tries the full row first, then
 * removes unknown columns one at a time on PGRST204 and retries.
 */
async function insertWithFallback(table, row, maxRetries = 20) {
  let current = { ...row };
  for (let i = 0; i < maxRetries; i++) {
    if (Object.keys(current).length === 0) break;
    const { error } = await supabase.from(table).insert([current]);
    if (!error) return { error: null };
    if (error.code === 'PGRST204') {
      const match = error.message.match(/Could not find the '(\w+)' column/);
      if (match) {
        console.warn(`Column '${match[1]}' missing in DB — skipping it`);
        const { [match[1]]: _dropped, ...rest } = current;
        current = rest;
        continue;
      }
    }
    return { error };
  }
  return { error: null }; // all optional columns removed — row still inserted (id + timestamps)
}

export async function submitQuote(quoteData) {
  if (!supabase) {
    console.warn('Supabase not configured — quote saved locally only');
    return { data: { id: 'local-' + Date.now() }, error: null };
  }

  const materialName = quoteData.colorSelection
    ? `${quoteData.materialName || ''} - ${quoteData.colorSelection}`.trim()
    : quoteData.materialName || null;

  // Insert into `quotes` — the canonical table for all customer inquiries.
  // This is what the CRM reads via fetchQuotes() and what Stripe references via lead_id.
  // Column names matched to the live quotes table schema
  const dbRow = {
    customer_name: quoteData.customerName || null,
    customer_email: quoteData.customerEmail || null,
    customer_phone: quoteData.customerPhone || null,
    customer_address: quoteData.customerAddress || null,
    customer_notes: quoteData.customerNotes || null,
    deck_sqft: quoteData.deckSqFt || null,
    material_id: quoteData.materialId || null,
    material_name: materialName,
    color_selection: quoteData.colorSelection || null,
    railing_type: quoteData.railingType || null,
    railing_lf: quoteData.railingLf || null,
    extras: quoteData.extras || [],
    photo_urls: quoteData.photoUrls || [],
    estimate_low: quoteData.estimateLow || null,
    estimate_high: quoteData.estimateHigh || null,
    estimate_breakdown: quoteData.estimateBreakdown || null,
    total_price: quoteData.estimateHigh || null,
    step_reached: 3,
    completed: true,
    market_slug: quoteData.market || null,
    trade_slug: quoteData.trade || null,
    trade: quoteData.trade || null,
  };

  // Try quotes table with column fallback for schema resilience.
  // Anon users can INSERT but not SELECT (RLS), so we can't read back the ID.
  let insertError = null;

  // First try direct insert (fastest path)
  const { error: directError } = await supabase.from('quotes').insert([dbRow]);

  if (directError) {
    console.warn('Direct quotes insert failed:', directError.message, '— trying with column fallback');
    // Fall back to column-stripping insert
    const { error: fallbackError } = await insertWithFallback('quotes', dbRow);
    insertError = fallbackError;
  }

  if (insertError) {
    console.error('Failed to save quote after fallback:', insertError);
    return { data: null, error: insertError };
  }

  // Quote saved successfully (ID not available for anon users due to RLS)
  const quoteId = null;

  // Fire notification with full estimate data for email/SMS
  notifyOwner({
    ...dbRow,
    estimateLow: quoteData.estimateLow,
    estimateHigh: quoteData.estimateHigh,
    estimate_low: quoteData.estimateLow,
    estimate_high: quoteData.estimateHigh,
    market_slug: quoteData.market,
    trade_slug: quoteData.trade,
    trade_display: quoteData.trade,
  }).catch(err => console.error('Notification failed:', err));

  return { data: { id: quoteId, ...dbRow }, error: null };
}

/**
 * Save partial quote progress for abandonment tracking.
 */
export async function saveQuoteProgress(partialData) {
  if (!supabase) return null;

  // Anon users can't upsert (needs SELECT), so just insert new rows for progress tracking
  if (!partialData.quoteId) {
    const { error } = await insertWithFallback('leads', {
      customer_email: partialData.customerEmail || null,
      estimated_sqft: partialData.deckSqFt || null,
      materials_selected: partialData.materialName ? { name: partialData.materialName } : null,
      source: 'website',
    });

    if (error) {
      console.error('Failed to save progress:', error);
      return null;
    }
    return { id: null }; // Can't read back the ID due to RLS
  }
  return { id: partialData.quoteId };
}

/**
 * Call the notify-quote edge function to text/email the owner.
 */
async function notifyOwner(quoteRecord) {
  if (!supabase) return;

  const { error } = await supabase.functions.invoke('notify-quote', {
    body: { quoteId: quoteRecord.id, record: quoteRecord },
  });

  if (error) {
    console.error('notify-quote edge function error:', error);
  }
}

/**
 * Send a chat message through the Claude API proxy edge function.
 * Returns the assistant's response text.
 */
export async function sendChatMessage(messages, quoteContext, marketContext) {
  if (!supabase) {
    return null; // Caller falls back to local mock
  }

  const { data, error } = await supabase.functions.invoke('chat', {
    body: {
      messages,
      quoteContext,
      marketSlug: marketContext?.marketSlug || '',
      marketDisplay: marketContext?.marketDisplay || 'your area',
      marketState: marketContext?.marketState || '',
      tradeDisplay: marketContext?.tradeDisplay || 'home improvement',
      buildingCodes: marketContext?.buildingCodes || '',
      pricingNotes: marketContext?.pricingNotes || '',
      licenseNote: marketContext?.licenseNote || '',
    },
  });

  if (error) {
    console.error('Chat edge function error:', error);
    return null;
  }

  return data?.response || null;
}

/**
 * Save a chat session to the database.
 */
export async function saveChatSession(sessionData) {
  if (!supabase) return null;

  // Chat sessions require auth to read/write — skip silently for anon users
  const { data: userData } = await supabase.auth.getUser();
  if (!userData?.user) return null;

  const { data, error } = await supabase
    .from('chat_sessions')
    .upsert([{
      id: sessionData.id || undefined,
      session_type: sessionData.sessionType || 'customer',
      quote_id: sessionData.quoteId || null,
      messages: sessionData.messages,
      message_count: sessionData.messages.length,
      escalated: sessionData.escalated || false,
      escalation_reason: sessionData.escalationReason || null,
    }], { onConflict: 'id' })
    .select()
    .single();

  if (error) {
    console.error('Failed to save chat session:', error);
    return null;
  }
  return data;
}

// ─── WEEK 2-3: MONEY TRACKER ───

/**
 * Upload a file to Supabase Storage and return its public URL.
 */
export async function uploadFile(bucket, filePath, file) {
  if (!supabase) return null;

  const { error } = await supabase.storage
    .from(bucket)
    .upload(filePath, file, { upsert: true });

  if (error) {
    console.error('File upload failed:', error);
    return null;
  }

  const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(filePath);
  return urlData?.publicUrl || null;
}

/**
 * Scan a receipt using Claude Vision edge function.
 */
export async function scanReceipt(fileBase64, fileType, fileName, projectId, jobCode) {
  if (!supabase) {
    console.warn('Supabase not configured — receipt scan unavailable');
    return null;
  }

  const { data, error } = await supabase.functions.invoke('scan-receipt', {
    body: { fileBase64, fileType, fileName, projectId, jobCode },
  });

  if (error) {
    console.error('scan-receipt error:', error);
    return null;
  }

  return data;
}

/**
 * Fetch all receipts, optionally filtered by project.
 */
export async function fetchReceipts(projectId) {
  if (!supabase) return [];

  let query = supabase
    .from('receipts')
    .select('*')
    .order('created_at', { ascending: false });

  if (projectId) {
    query = query.eq('project_id', projectId);
  }

  const { data, error } = await query;
  if (error) { console.error('fetchReceipts error:', error); return []; }
  return data || [];
}

/**
 * Assign a receipt to a project.
 */
export async function assignReceipt(receiptId, projectId, jobCode) {
  if (!supabase) return null;

  const { data, error } = await supabase
    .from('receipts')
    .update({ project_id: projectId, job_code: jobCode, status: 'assigned' })
    .eq('id', receiptId)
    .select()
    .single();

  if (error) { console.error('assignReceipt error:', error); return null; }
  return data;
}

/**
 * Fetch all projects.
 */
export async function fetchProjects() {
  if (!supabase) return [];

  const { data, error } = await supabase
    .from('projects')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) { console.error('fetchProjects error:', error); return []; }
  return data || [];
}

/**
 * Fetch all invoices.
 */
export async function fetchInvoices() {
  if (!supabase) return [];

  const { data, error } = await supabase
    .from('invoices')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) { console.error('fetchInvoices error:', error); return []; }
  return data || [];
}

/**
 * Create a new invoice.
 */
export async function createInvoice(invoiceData) {
  if (!supabase) return null;

  const { data, error } = await supabase
    .from('invoices')
    .insert([invoiceData])
    .select()
    .single();

  if (error) { console.error('createInvoice error:', error); return null; }
  return data;
}

/**
 * Update invoice status (draft → sent → paid).
 */
export async function updateInvoiceStatus(invoiceId, status, extras = {}) {
  if (!supabase) return null;

  const updateData = { status, ...extras };
  if (status === 'sent') updateData.sent_at = new Date().toISOString();
  if (status === 'paid') updateData.paid_at = new Date().toISOString();

  const { data, error } = await supabase
    .from('invoices')
    .update(updateData)
    .eq('id', invoiceId)
    .select()
    .single();

  if (error) { console.error('updateInvoiceStatus error:', error); return null; }
  return data;
}

/**
 * Fetch all quotes (for CRM / admin views).
 */
export async function fetchQuotes(filters = {}) {
  if (!supabase) return [];

  let query = supabase
    .from('quotes')
    .select('*')
    .order('created_at', { ascending: false });

  if (filters.completed !== undefined) {
    query = query.eq('completed', filters.completed);
  }
  if (filters.status) {
    query = query.eq('status', filters.status);
  }

  const { data, error } = await query;
  if (error) { console.error('fetchQuotes error:', error); return []; }
  return data || [];
}

/**
 * Fetch the current pricing model.
 */
export async function fetchPricingModel() {
  if (!supabase) return [];

  const { data, error } = await supabase
    .from('pricing_model')
    .select('*')
    .order('material_key');

  if (error) { console.error('fetchPricingModel error:', error); return []; }
  return data || [];
}

/**
 * Fetch documents from the documents table.
 */
export async function fetchDocuments() {
  if (!supabase) return [];

  const { data, error } = await supabase
    .from('documents')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) { console.error('fetchDocuments error:', error); return []; }
  return data || [];
}

/**
 * Save an analyzed document.
 */
export async function saveDocument(docData) {
  if (!supabase) return null;

  const { data, error } = await supabase
    .from('documents')
    .insert([docData])
    .select()
    .single();

  if (error) { console.error('saveDocument error:', error); return null; }
  return data;
}

export async function deleteDocument(docId, fileUrl) {
  if (!supabase) return false;

  // Delete from storage if we have a URL
  if (fileUrl) {
    const path = fileUrl.split('/storage/v1/object/public/')[1];
    if (path) {
      const [bucket, ...rest] = path.split('/');
      await supabase.storage.from(bucket).remove([rest.join('/')]);
    }
  }

  // Delete from database
  const { error } = await supabase.from('documents').delete().eq('id', docId);
  if (error) { console.error('deleteDocument error:', error); return false; }
  return true;
}

/**
 * Fetch abandoned quotes (incomplete, created > 30 min ago).
 */
export async function fetchAbandonedQuotes() {
  if (!supabase) return [];

  const thirtyMinAgo = new Date(Date.now() - 30 * 60 * 1000).toISOString();

  const { data, error } = await supabase
    .from('quotes')
    .select('*')
    .eq('completed', false)
    .lt('created_at', thirtyMinAgo)
    .order('created_at', { ascending: false });

  if (error) { console.error('fetchAbandonedQuotes error:', error); return []; }
  return data || [];
}

// ─── OWNER AI ASSISTANT ───

/**
 * Send a message to the owner chatbot (has full DB read access).
 */
export async function sendOwnerChat(messages) {
  if (!supabase) return null;

  const { data, error } = await supabase.functions.invoke('owner-chat', {
    body: { messages },
  });

  if (error) {
    console.error('owner-chat error:', error);
    return null;
  }

  return data?.response || null;
}

/**
 * Trigger document analysis via the analyze-document edge function.
 */
export async function analyzeDocumentRemote(documentId, fileBase64, fileType, docType) {
  if (!supabase) return null;

  const { data, error } = await supabase.functions.invoke('analyze-document', {
    body: { documentId, fileBase64, fileType, docType },
  });

  if (error) {
    console.error('analyze-document error:', error);
    return null;
  }

  return data;
}

// ─── AUTH ───

export async function signIn(email, password) {
  if (!supabase) return { user: null, error: 'Supabase not configured' };
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) return { user: null, error: error.message };
  return { user: data.user, error: null };
}

export async function signOut() {
  if (!supabase) return;
  await supabase.auth.signOut();
}

export async function getUser() {
  if (!supabase) return null;
  const { data } = await supabase.auth.getUser();
  return data?.user || null;
}

export function onAuthStateChange(callback) {
  if (!supabase) return { unsubscribe: () => {} };
  const { data } = supabase.auth.onAuthStateChange((_event, session) => {
    callback(session?.user || null);
  });
  return data.subscription;
}

// ─── INVOICE OPERATIONS ───

export async function sendInvoice(invoiceId) {
  if (!supabase) return null;
  const { data, error } = await supabase.functions.invoke('send-invoice', {
    body: { invoiceId },
  });
  if (error) { console.error('send-invoice error:', error); return null; }
  return data;
}

export async function markInvoicePaid(invoiceId, { paymentMethod, paidAmount, paymentProofUrl }) {
  if (!supabase) return null;

  const updateData = {
    status: 'paid',
    paid_at: new Date().toISOString(),
    paid_amount: paidAmount,
    payment_method: paymentMethod || 'check',
  };

  // Store proof URL: use stripe_payment_link for Stripe, or pdf_url for manual proofs
  if (paymentProofUrl) {
    if (paymentMethod === 'stripe') {
      updateData.stripe_payment_link = paymentProofUrl;
    } else {
      updateData.pdf_url = paymentProofUrl; // reuse pdf_url for payment proof docs
    }
  }

  const { data, error } = await supabase
    .from('invoices')
    .update(updateData)
    .eq('id', invoiceId)
    .select()
    .single();
  if (error) { console.error('markInvoicePaid error:', error); return null; }
  return data;
}

export async function updateQuoteStatus(quoteId, status) {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('quotes')
    .update({ status })
    .eq('id', quoteId)
    .select()
    .single();
  if (error) { console.error('updateQuoteStatus error:', error); return null; }
  return data;
}

/**
 * Delete a quote by id. Admin-only — relies on RLS to enforce auth.
 * Returns { error } — error is null on success.
 */
export async function deleteQuote(quoteId) {
  if (!supabase) return { error: 'Supabase not configured' };
  const { error } = await supabase.from('quotes').delete().eq('id', quoteId);
  if (error) {
    console.error('deleteQuote error:', error);
    return { error: error.message };
  }
  return { error: null };
}

/**
 * Delete a project by id. Admin-only — relies on RLS to enforce auth.
 * Cascades to invoices/receipts/inspections via FK, depending on schema.
 */
export async function deleteProject(projectId) {
  if (!supabase) return { error: 'Supabase not configured' };
  const { error } = await supabase.from('projects').delete().eq('id', projectId);
  if (error) {
    console.error('deleteProject error:', error);
    return { error: error.message };
  }
  return { error: null };
}

/**
 * Generate a PDF (HTML) for an estimate or invoice.
 * Returns { html, url } where url is the Supabase Storage public link.
 */
export async function generateDocument(type, data) {
  if (!supabase) return null;
  const { data: result, error } = await supabase.functions.invoke('generate-pdf', {
    body: { type, data },
  });
  if (error) { console.error('generate-pdf error:', error); return null; }
  return result;
}

// ─── EMPLOYEE & TIMECARD FUNCTIONS ───

export async function fetchEmployees() {
  if (!supabase) return [];
  const { data, error } = await supabase.from('employees').select('*').order('name');
  if (error) { console.error('fetchEmployees error:', error); return []; }
  return data || [];
}

export async function createEmployee(empData) {
  if (!supabase) return null;
  const { data, error } = await supabase.from('employees').insert([empData]).select().single();
  if (error) { console.error('createEmployee error:', error); return null; }
  return data;
}

export async function updateEmployee(empId, updates) {
  if (!supabase) return null;
  const { data, error } = await supabase.from('employees').update(updates).eq('id', empId).select().single();
  if (error) { console.error('updateEmployee error:', error); return null; }
  return data;
}

export async function fetchTimecards(filters = {}) {
  if (!supabase) return [];
  let query = supabase.from('timecards').select('*').order('work_date', { ascending: false });
  if (filters.employeeId) query = query.eq('employee_id', filters.employeeId);
  if (filters.projectId) query = query.eq('project_id', filters.projectId);
  if (filters.startDate) query = query.gte('work_date', filters.startDate);
  if (filters.endDate) query = query.lte('work_date', filters.endDate);
  const { data, error } = await query.limit(100);
  if (error) { console.error('fetchTimecards error:', error); return []; }
  return data || [];
}

export async function parseTimecardFromText(message, employeeId) {
  if (!supabase) return null;
  const { data, error } = await supabase.functions.invoke('parse-timecard', {
    body: { message, employeeId, source: 'manual' },
  });
  if (error) { console.error('parse-timecard error:', error); return null; }
  return data;
}

export async function approveTimecard(timecardId) {
  if (!supabase) return null;
  const { data, error } = await supabase.from('timecards').update({ approved: true, approved_by: 'Owner' }).eq('id', timecardId).select().single();
  if (error) { console.error('approveTimecard error:', error); return null; }
  return data;
}

export async function deleteTimecard(timecardId) {
  if (!supabase) return false;
  const { error } = await supabase.from('timecards').delete().eq('id', timecardId);
  if (error) { console.error('deleteTimecard error:', error); return false; }
  return true;
}

// ─── CAPACITY & SCHEDULING ───

/**
 * Fetch capacity schedule for a market (or all markets).
 */
export async function fetchCapacity(marketSlug) {
  if (!supabase) return [];
  let query = supabase
    .from('capacity_schedule')
    .select('*')
    .order('week_start', { ascending: true });
  if (marketSlug) query = query.eq('market_slug', marketSlug);
  const { data, error } = await query;
  if (error) { console.error('fetchCapacity error:', error); return []; }
  return data || [];
}

/**
 * Fetch the revenue scaling config.
 */
export async function fetchRevenueConfig() {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('revenue_config')
    .select('config_value')
    .eq('config_key', 'scaling_schedule')
    .single();
  if (error) { console.error('fetchRevenueConfig error:', error); return null; }
  return data?.config_value || null;
}

/**
 * Update revenue scaling config.
 */
export async function updateRevenueConfig(config) {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('revenue_config')
    .update({ config_value: config })
    .eq('config_key', 'scaling_schedule')
    .select()
    .single();
  if (error) { console.error('updateRevenueConfig error:', error); return null; }
  return data;
}


// ─── DOCUSIGN CONTRACT ───

/**
 * Send a fixed-bid contract for e-signature via DocuSign.
 */
export async function sendContract(contractData) {
  if (!supabase) return null;
  const { data, error } = await supabase.functions.invoke('send-contract', {
    body: contractData,
  });
  if (error) { console.error('send-contract error:', error); return null; }
  return data;
}

// ─── DESIGN & PERMITTING ───

/**
 * Update project design/plans fields.
 */
export async function updateProjectPlans(projectId, updates) {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('projects')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', projectId)
    .select()
    .single();
  if (error) { console.error('updateProjectPlans error:', error); return null; }
  return data;
}

/**
 * Update project permit fields.
 */
export async function updateProjectPermit(projectId, updates) {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('projects')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', projectId)
    .select()
    .single();
  if (error) { console.error('updateProjectPermit error:', error); return null; }
  return data;
}

/**
 * Fetch inspections for a project.
 */
export async function fetchInspections(projectId) {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('inspections')
    .select('*')
    .eq('project_id', projectId)
    .order('scheduled_date', { ascending: true });
  if (error) { console.error('fetchInspections error:', error); return []; }
  return data || [];
}

/**
 * Create a new inspection.
 */
export async function createInspection(inspectionData) {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('inspections')
    .insert([inspectionData])
    .select()
    .single();
  if (error) { console.error('createInspection error:', error); return null; }
  return data;
}

/**
 * Update inspection status/results.
 */
export async function updateInspection(inspectionId, updates) {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('inspections')
    .update(updates)
    .eq('id', inspectionId)
    .select()
    .single();
  if (error) { console.error('updateInspection error:', error); return null; }
  return data;
}

// ─── WORKER APPLICATIONS ───

/**
 * Submit a contractor/worker application to the workers table.
 */
export async function submitWorkerApplication(applicationData) {
  if (!supabase) {
    console.log('Worker application (Supabase not configured):', applicationData);
    return { data: { id: 'local-' + Date.now() }, error: null };
  }

  const row = {
    full_name: applicationData.fullName || null,
    email: applicationData.email || null,
    phone: applicationData.phone || null,
    market: applicationData.market || null,
    trades: applicationData.trades || [],
    years_experience: applicationData.yearsExperience ? parseInt(applicationData.yearsExperience, 10) : null,
    has_tools: applicationData.hasTools === 'yes',
    has_vehicle: applicationData.hasVehicle === 'yes',
    has_insurance: applicationData.hasInsurance === 'yes',
    desired_rate: applicationData.desiredRate ? parseFloat(applicationData.desiredRate) : null,
    experience_desc: applicationData.experienceDesc || null,
    photo_count: applicationData.photoCount || 0,
    status: 'applied',
  };

  const { error } = await supabase.from('workers').insert([row]);

  if (error) {
    console.error('submitWorkerApplication error:', error);
    return { data: null, error: error.message };
  }

  console.log('Worker application submitted:', applicationData.email);
  return { data: { id: 'submitted-' + Date.now() }, error: null };
}

// ─── STRIPE PAYMENT ───

/**
 * Create a Stripe Checkout session for the 16.5% signing deposit.
 * The edge function saves the lead to Supabase (service role) and returns
 * the Stripe checkout URL to redirect the customer to.
 *
 * @param {Object} params
 * @param {number}  params.total_price       - Full project estimate in dollars
 * @param {string}  params.market            - Market slug (e.g. 'boise')
 * @param {string}  params.trade             - Trade slug (e.g. 'decks')
 * @param {string}  params.customer_email
 * @param {string}  params.customer_name
 * @param {string}  params.customer_phone
 * @param {Object}  params.lead_data         - Full quote payload (same shape as submitQuote input)
 * @returns {{ sessionId: string, url: string, quoteId: string, depositAmount: number } | null}
 */
export async function createCheckoutSession({
  total_price,
  market,
  trade,
  customer_email,
  customer_name,
  customer_phone,
  lead_data,
}) {
  if (!supabase) {
    console.warn('Supabase not configured — Stripe checkout unavailable');
    return null;
  }

  const { data, error } = await supabase.functions.invoke('create-checkout-session', {
    body: {
      total_price,
      market,
      trade,
      customer_email,
      customer_name,
      customer_phone,
      lead_data,
    },
  });

  if (error) {
    console.error('create-checkout-session error:', error);
    return null;
  }

  console.log('create-checkout-session response:', data);

  // Handle case where data contains an error from the edge function
  if (data?.error) {
    console.error('Checkout session error:', data.error, data.details);
    return null;
  }

  return data;
}
